import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Grid, Stack } from '@mui/material';
import {
  arrayToCommaSeparatedString,
  booleanToYesNo,
  findLabelByValue,
  findOrFormatEnum,
  formatDateTime,
  formatEnum,
} from 'utils/stringUtil';
import ContactNote from 'types/ContactNote';
import { GlobalState } from 'reducers/stateTypes';
import { JBIcon, JBSection, JBTitleValueGrid, JBTypography } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { translateContactTypeEnum } from 'utils/contactNoteUtil';
import { DataTable } from 'components/Common/DataTable/DataTable';
import { RelatedPartnersHeadcells } from 'views/ContactNoteDetails/Components/RelatedPartnersHeadcells';
import { AdditionalAttendeeHeadcells } from 'views/ContactNoteDetails/Components/AdditionalAttendeeHeadcells';
import { ContactType } from 'views/ContactNoteForm/components/data/contactTypeOptions';
import { FileInfo } from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';
import DocumentFileRow from 'views/ContactNoteForm/components/DocumentUpload/DocumentFileRow';
import { NOOP } from 'utils/utils';
import { EnvConfig } from 'config/envConfig';

import ContactNoteAuditHistory from './ContactNoteAuditHistory';

/**
 * The "ContactNote summary" section of the contactNote details view
 * @param props.contactNote The contactNote to show the details of.
 */
const ContactNoteDetailsGeneral = (props: { contactNote: ContactNote }) => {
  const startDateTime = formatDateTime(props.contactNote.startDateTime);
  const endDateTime = formatDateTime(props.contactNote.endDateTime);
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const isSourceTravelTool = props.contactNote.source === 'MYTRAVEL';
  const responsibleRM = `${props.contactNote.partner.relationshipManager.fullName} (${props.contactNote.partner.relationshipManager.uid})`;
  const tripId = props.contactNote.tripId ?? '-';
  const isTripIdDisplayed = !!props.contactNote.tripId;
  const usedInBr = useSelector(selectIsUsedInBr);
  const { t } = useTranslation();
  useSelector((state: GlobalState) => state.contactNoteList.filters);

  return (
    <JBSection title={t('noteForm.title', 'Contact note')} avatar={<JBIcon iconName="contactNote" size="xl" />}>
      <Grid container>
        <Grid container>
          <Grid item xs={12} mb={4}>
            <Divider textAlign="left">{t('noteForm.partnerReference', 'Partner reference')}</Divider>
          </Grid>

          {!usedInBr && (
            <Grid container>
              <JBTitleValueGrid
                title={
                  isSourceTravelTool
                    ? t('noteForm.visitedPartner', 'Visited partner')
                    : t('noteForm.partner', 'Partner')
                }
                value={props.contactNote.partnerFullName}
                gridSize={isSourceTravelTool ? 6 : 12}
              />
              {isSourceTravelTool && (
                <JBTitleValueGrid
                  title={t('noteForm.responsibleRm', 'Responsible RM')}
                  value={responsibleRM}
                  gridSize={6}
                />
              )}
            </Grid>
          )}

          <Grid item xs={12} sx={{ marginTop: '8px' }}>
            <JBTypography variant="caption">
              {usedInBr
                ? t('noteForm.relatedPartners', 'Related partners')
                : t('noteForm.attendeesClients', 'Attendees clients/prospects')}
            </JBTypography>
            <DataTable
              headCells={RelatedPartnersHeadcells}
              rows={
                props.contactNote.relatedPartners?.map((item) => {
                  return { ...item, id: item.partnerUId };
                }) || []
              }
            />
          </Grid>
        </Grid>

        <Grid item xs={12} my={4}>
          <Divider textAlign="left">{t('noteForm.attendees', 'Attendees')}</Divider>
        </Grid>
        <Grid item xs={12}>
          <JBTypography variant="caption">
            {isSourceTravelTool
              ? t('noteForm.additionalTravelers', 'Additional travelers')
              : t('noteForm.attendeesBank', 'Attendees (bank)')}
          </JBTypography>
        </Grid>

        <Grid container>
          <DataTable
            emptyMessage={t('noteForm.noAdditionalTravelersAttendees', 'No additional travelers/attendees.')}
            headCells={AdditionalAttendeeHeadcells}
            rows={
              props.contactNote.additionalAttendees?.map((item) => {
                return { ...item, id: item.userId };
              }) || []
            }
          />
        </Grid>

        <Grid item xs={12} my={4}>
          <Divider textAlign="left">{t('noteForm.contactDetails.title', 'Contact details')} </Divider>
        </Grid>

        <Stack sx={{ width: '100%' }} spacing={2}>
          <Grid container>
            <JBTitleValueGrid
              title={t('noteForm.contactDetails.subject', 'Subject')}
              value={props.contactNote.subject}
              gridSize={12}
            />
          </Grid>

          <Grid container>
            <JBTitleValueGrid
              title={t('noteForm.contactDetails.contactType', 'Contact type')}
              value={translateContactTypeEnum(props.contactNote.contactType)}
              gridSize={isTripIdDisplayed ? 4 : 6}
            />

            <JBTitleValueGrid
              title={t('noteForm.contactDetails.source', 'Source')}
              value={formatEnum(props.contactNote.source)}
              gridSize={isTripIdDisplayed ? 4 : 6}
            />

            {isTripIdDisplayed && (
              <JBTitleValueGrid title={t('noteForm.contactDetails.tripId', 'Trip ID')} value={tripId} gridSize={4} />
            )}
          </Grid>

          {(props.contactNote.contactType === ContactType.PHONE_CALL ||
            props.contactNote.contactType === ContactType.CHAT) && (
            <Grid container>
              <JBTitleValueGrid
                title={t('noteForm.contactDetails.startDateTime', 'Meeting start date & time')}
                value={startDateTime}
                gridSize={6}
              />

              <JBTitleValueGrid
                title={t('noteForm.contactDetails.endDateTime', 'Meeting end date & time')}
                value={endDateTime}
                gridSize={6}
              />
            </Grid>
          )}

          {(props.contactNote.contactType === ContactType.EMAIL ||
            props.contactNote.contactType === ContactType.FAX) && (
            <Grid container>
              <JBTitleValueGrid
                title={t('noteForm.contactDetails.submissionDateTime', 'Submission date & time')}
                value={startDateTime}
                gridSize={6}
              />

              <JBTitleValueGrid
                title={t('noteForm.contactDetails.receptionDateTime', 'Reception date & time')}
                value={endDateTime}
                gridSize={6}
              />
            </Grid>
          )}

          {(props.contactNote.contactType === ContactType.EVENT ||
            props.contactNote.contactType === ContactType.OTHER ||
            props.contactNote.contactType === ContactType.MEETING) && (
            <Grid container>
              <JBTitleValueGrid
                title={t('noteForm.contactDetails.startDateTime', 'Meeting start date & time')}
                value={startDateTime}
                gridSize={6}
              />

              <JBTitleValueGrid
                title={t('noteForm.contactDetails.endDateTime', 'Meeting end date & time')}
                value={endDateTime}
                gridSize={6}
              />

              <JBTitleValueGrid
                title={t('noteForm.contactDetails.locationCountry', 'Country')}
                value={findOrFormatEnum(findLabelByValue(props.contactNote.locationCountry, countryOptions))}
                gridSize={6}
              />

              <JBTitleValueGrid
                title={t('noteForm.contactDetails.locationCity', 'Location(City)')}
                value={props.contactNote.locationCity}
                gridSize={6}
              />
            </Grid>
          )}

          <Grid container>
            <JBTitleValueGrid
              title={t('noteForm.contactDetails.qualifiedContact', 'Qualified client contact')}
              gridSize={6}
              value={booleanToYesNo(props.contactNote.qualifiedClientContact)}
            />

            <JBTitleValueGrid
              title={t('noteForm.contactDetails.crossBorderActivity', 'Cross border activity')}
              value={booleanToYesNo(props.contactNote.crossBorderActivity)}
              gridSize={6}
            />
          </Grid>

          <Grid container>
            <JBTitleValueGrid
              title={t('noteForm.contactDetails.topic', 'Topic')}
              value={formatEnum(arrayToCommaSeparatedString(props.contactNote.topics))}
              gridSize={6}
            />

            <JBTitleValueGrid
              title={t('noteForm.contactDetails.initiatedBy', 'Initiated by')}
              value={formatEnum(props.contactNote.initiatedBy)}
              gridSize={6}
            />
          </Grid>

          <Grid container>
            <JBTitleValueGrid
              title={t('noteForm.contactDetails.notes', 'Notes')}
              value={props.contactNote.notes}
              gridSize={12}
            />
          </Grid>
        </Stack>
      </Grid>
      <Grid container>
        <Grid item xs={12} my={4}>
          <Divider textAlign="left"> Documents </Divider>
        </Grid>
        <Grid item xs={12}>
          <JBTypography variant="caption">Attachments</JBTypography>
        </Grid>
        <Grid item xs={5}>
          {!props.contactNote.attachments || props.contactNote.attachments.length === 0 ? (
            <Box mt="16px">
              <JBTypography variant="body1">No attachments added.</JBTypography>
            </Box>
          ) : (
            props.contactNote.attachments.map((attachment: FileInfo, i: number) => (
              <DocumentFileRow
                key={i}
                index={i}
                fileObject={attachment}
                fileObjects={[attachment]}
                setFileObjects={NOOP}
                viewOnly
              />
            ))
          )}
        </Grid>
      </Grid>
      {EnvConfig.config.auditApiEnabled && <ContactNoteAuditHistory contactNote={props.contactNote} />}
    </JBSection>
  );
};

export default ContactNoteDetailsGeneral;
